/**
 * EditContactHandleModal
 *
 * Modal for editing a carrier contact handle from within transmission panel.
 * Reuses shared ContactHandleForm component.
 */

import { Loader2, Pencil } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useUpdateHandleMutation } from '@/services/api/carriersApiService';

import type { RecipientContact } from '../../types';
import {
  ContactHandleForm,
  ContactHandleFormState,
  INITIAL_FORM_STATE,
} from './ContactHandleForm';

interface EditContactHandleModalProps {
  open: boolean;
  onClose: () => void;
  contact: RecipientContact;
}

const getTitle = (handleType: string): string => {
  switch (handleType) {
    case 'FAX':
      return 'Edit Fax Number';
    case 'EMAIL':
      return 'Edit Email';
    case 'MAILING_ADDRESS':
      return 'Edit Mailing Address';
    case 'PHONE':
      return 'Edit Phone';
    default:
      return 'Edit Contact';
  }
};

export const EditContactHandleModal: React.FC<EditContactHandleModalProps> = ({
  open,
  onClose,
  contact,
}) => {
  const [updateHandle, { isLoading }] = useUpdateHandleMutation();
  const [formState, setFormState] =
    useState<ContactHandleFormState>(INITIAL_FORM_STATE);

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      setFormState({
        handleType: contact.handleType as ContactHandleFormState['handleType'],
        value: contact.faxNumber || contact.email || '',
        label: contact.label || '',
        addressLine1: contact.address?.address_line1 || '',
        addressLine2: contact.address?.address_line2 || '',
        city: contact.address?.city || '',
        state: contact.address?.state || '',
        zip: contact.address?.zip_code || '',
      });
    }
  }, [open, contact]);

  const handleSave = async () => {
    const isAddress = formState.handleType === 'MAILING_ADDRESS';
    try {
      await updateHandle({
        handleId: contact.handleId,
        updates: {
          name: formState.label.trim() || undefined,
          ...(isAddress
            ? {
                addressLine1: formState.addressLine1.trim(),
                addressLine2: formState.addressLine2.trim() || undefined,
                city: formState.city.trim(),
                state: formState.state,
                zip: formState.zip.trim(),
              }
            : { value: formState.value.trim() }),
        },
      }).unwrap();

      toast.success('Contact updated');
      onClose();
    } catch (error) {
      console.error('Failed to update contact:', error);
      toast.error('Failed to update contact');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-zinc-500" />
            {getTitle(contact.handleType)}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Directory update notice */}
          <div className="flex items-start gap-2 px-3 py-2 bg-amber-50/50 border border-amber-100 rounded-lg">
            <span className="text-[11px] text-amber-700">
              Changes will update{' '}
              <span className="font-medium">{contact.source.entityName}</span>{' '}
              in the carrier directory.
            </span>
          </div>

          <ContactHandleForm
            state={formState}
            onChange={setFormState}
            showTypeSelector={false}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isLoading}>
            {isLoading && (
              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
            )}
            Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
